import IconChevronRight from 'assets/icons/chevron-right.svg';
import Button from 'components/button-new';
import { CollectionProduct } from 'models/collection/collection';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { PickyCatsTip } from 'modules/picky-cats/types';

interface ResultWithTipsProps {
  title: string;
  subtitle: string;
  tips: PickyCatsTip[];
  products: CollectionProduct[];
  totalCount: number;
  ctaLabel: string;
  onCtaClick: () => void;
  onProductClick?: (productId: string) => void;
}

/** Picky Cats end-of-flow result screen: educational tips plus recommended products that link to their PDP. */
const ResultWithTips: FC<ResultWithTipsProps> = ({
  title,
  subtitle,
  tips,
  products,
  totalCount,
  ctaLabel,
  onCtaClick,
  onProductClick,
}) => (
  <div className="flex flex-col gap-6 px-4 pt-4 pb-8" data-test="picky-cats-result-tips">
    <div className="flex flex-col items-center gap-1 text-center">
      <p className="mindset-28 text-text-light-bg-primary" data-test="picky-cats-result-tips-title">
        {title}
      </p>
      <p
        className="text-text-light-bg-secondary text-base"
        data-test="picky-cats-result-tips-subtitle"
      >
        {subtitle}
      </p>
    </div>

    <ol
      className="bg-turquoise-50 flex flex-col gap-4 rounded-2xl px-4 py-4"
      data-test="picky-cats-result-tips-list"
    >
      {tips.map((tip, index) => (
        <li
          key={tip.icon}
          className="flex items-start gap-3"
          data-test={`picky-cats-result-tips-tip-${index}`}
        >
          <span className="text-text-light-bg-primary text-base font-bold">{index + 1}.</span>
          <span
            className="text-text-light-bg-primary text-base"
            data-test={`picky-cats-result-tips-tip-${index}-text`}
          >
            {tip.text}
          </span>
        </li>
      ))}
    </ol>

    {products.length > 0 && (
      <div className="flex flex-col gap-3" data-test="picky-cats-result-tips-products">
        <p
          className="text-text-light-bg-primary text-base font-bold"
          data-test="picky-cats-result-tips-products-heading"
        >
          <FormattedMessage
            id="picky-cats:result:recommendation-heading"
            defaultMessage="Recommendation for you"
          />
        </p>
        {products.map((product) => {
          const weight = product.getWeights()[0];

          return (
            <Link
              key={product.id}
              href={`/products/${product.handle}`}
              onClick={() => onProductClick?.(product.id)}
              className="border-input-light-bg-outline-default bg-background-paper flex items-center gap-3 rounded-lg border p-2"
              data-test={`picky-cats-result-tips-product-${product.id}`}
            >
              <div className="relative size-12 shrink-0 overflow-hidden rounded-md">
                {product.image.src && (
                  <Image
                    src={product.image.src}
                    alt=""
                    fill
                    sizes="48px"
                    className="object-cover"
                  />
                )}
              </div>
              <div className="flex min-w-0 grow flex-col gap-1">
                <p
                  className="text-text-light-bg-primary truncate text-sm font-bold"
                  data-test={`picky-cats-result-tips-product-${product.id}-title`}
                >
                  {product.title}
                </p>
                {weight && (
                  <p
                    className="text-text-light-bg-secondary text-sm"
                    data-test={`picky-cats-result-tips-product-${product.id}-weight`}
                  >
                    {weight}
                  </p>
                )}
              </div>
              <IconChevronRight
                className="text-text-light-bg-secondary size-3 shrink-0"
                aria-hidden
              />
            </Link>
          );
        })}
      </div>
    )}

    <Button
      size="large"
      iconRight
      className="w-full"
      onClick={onCtaClick}
      data-test="picky-cats-result-tips-cta"
    >
      {totalCount > 0 ? `${ctaLabel} (${totalCount})` : ctaLabel}
    </Button>
  </div>
);

export default ResultWithTips;
